<template>
  <v-card>
    <v-card-title>
      <span class="text-h5">{{ title }}</span>
    </v-card-title>

    <v-card-text>
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-divider class="mb-4" />
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.basic_information') }}
            </h3>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.name"
              :label="$t('rate_plans.name')"
              :rules="[v => !!v || $t('rate_plans.name_required')]"
              required
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.code"
              :label="$t('rate_plans.code')"
              :rules="[v => !!v || $t('rate_plans.code_required')]"
              :readonly="isEditing"
              required
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="formData.description"
              :label="$t('rate_plans.description')"
              rows="3"
              auto-grow
              clearable
              :hint="$t('rate_plans.description_hint')"
            />
          </v-col>

          <v-col cols="12">
            <v-divider class="mb-4" />
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.pricing_configuration') }}
            </h3>
          </v-col>

          <v-col cols="12" sm="4">
            <v-text-field
              v-model.number="formData.pricing_offset"
              :label="pricingOffsetLabel"
              type="number"
              :rules="pricingOffsetRules"
              :hint="pricingOffsetHint"
              persistent-hint
              required
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="formData.units"
              :items="unitsOptions"
              :label="$t('rate_plans.units')"
              item-text="text"
              item-value="value"
              :rules="[v => !!v || $t('rate_plans.units_required')]"
              required
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              v-model="formData.meal_plan"
              :items="mealPlanOptions"
              :label="$t('rate_plans.meal_plan')"
              item-text="text"
              item-value="value"
              :hint="$t('rate_plans.meal_plan_hint')"
              disabled
              persistent-hint
            />
          </v-col>

          <v-col cols="12">
            <v-switch
              v-model="restrictionsOnly"
              :label="$t('rate_plans.restrictions_only')"
              :hint="$t('rate_plans.restrictions_only_hint')"
              persistent-hint
            />
          </v-col>

          <v-col cols="12">
            <v-divider class="mb-4" />
            <h3 class="text-h6 mb-3">
              {{ $t('rate_plans.channel_configuration') }}
            </h3>
          </v-col>

          <v-col cols="12">
            <v-autocomplete
              v-model="formData.applicable_channels"
              :items="simpleBookingExternalListingItems"
              :label="$t('rate_plans.applicable_channels')"
              :hint="$t('rate_plans.applicable_channels_hint')"
              item-text="text"
              item-value="value"
              multiple
              chips
              clearable
              persistent-hint
              :loading="simpleBookingChannelListingsLoading"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn text :disabled="loading" @click="closeModal">{{
        $t('Cancel')
      }}</v-btn>
      <v-btn color="primary" :loading="loading" @click="save">
        {{ $t('Save') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'SimpleBookingRatePlanModal',

  props: {
    ratePlan: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      formData: {
        name: '',
        code: '',
        description: '',
        pricing_offset: 0,
        units: 'percentage',
        meal_plan: 'default',
        applicable_channels: [],
        pms_type: 'simple_booking',
        extra_data: {
          simple_booking: {
            restrictions_only: false,
          },
        },
      },
      mealPlanOptions: [
        { text: 'Room Only (No Meals)', value: 'default' },
        { text: 'Bed & Breakfast', value: 'breakfast' },
        { text: 'Half Board (Breakfast & Dinner)', value: 'half-board' },
        { text: 'Full Board (All Meals)', value: 'full-board' },
        { text: 'All Inclusive', value: 'all-inclusive' },
      ],
    }
  },

  computed: {
    ...mapState('simpleBooking', ['channelListings', 'loading']),

    isEditing() {
      return !!this.ratePlan
    },
    title() {
      return this.isEditing
        ? this.$t('rate_plans.edit_simple_booking_rate_plan')
        : this.$t('rate_plans.create_simple_booking_rate_plan')
    },

    restrictionsOnly: {
      get() {
        return !!this.formData?.extra_data?.simple_booking?.restrictions_only
      },
      set(value) {
        if (!this.formData.extra_data) {
          this.$set(this.formData, 'extra_data', {})
        }
        if (!this.formData.extra_data.simple_booking) {
          this.$set(this.formData.extra_data, 'simple_booking', {})
        }
        this.$set(
          this.formData.extra_data.simple_booking,
          'restrictions_only',
          !!value
        )
      },
    },

    simpleBookingChannelListingsLoading() {
      return this.loading?.channelListings || false
    },

    simpleBookingExternalListingItems() {
      const listings = this.channelListings || []

      return listings
        .filter(l => l?.active && l?.external_listing_id)
        .map(l => ({
          value: l.external_listing_id,
          text: l.listing_name
            ? `${l.listing_name} (${l.external_listing_id})`
            : l.external_listing_id,
        }))
    },

    currencySign() {
      const locale = window.config?.locale || 'US'
      const currency = window.config?.currency || 'USD'
      try {
        const formatter = new Intl.NumberFormat(`en-${locale}`, {
          style: 'currency',
          currency: currency,
          maximumFractionDigits: 0,
        })
        return (
          formatter.formatToParts(0).find(c => c.type === 'currency')?.value ||
          '$'
        )
      } catch (error) {
        return '$'
      }
    },

    unitsOptions() {
      return [
        { text: '%', value: 'percentage' },
        { text: this.currencySign, value: 'fixed' },
      ]
    },

    pricingOffsetLabel() {
      if (this.formData.units === 'fixed') {
        return `${this.$t('rate_plans.pricing_offset')} (${this.currencySign})`
      }
      return `${this.$t('rate_plans.pricing_offset')} (%)`
    },

    pricingOffsetRules() {
      if (this.formData.units === 'fixed') {
        return [
          v => !!v || v === 0 || this.$t('rate_plans.pricing_offset_required'),
        ]
      }
      return [
        v => !!v || v === 0 || this.$t('rate_plans.pricing_offset_required'),
        v =>
          (v >= -100 && v <= 100) || this.$t('rate_plans.pricing_offset_range'),
      ]
    },

    pricingOffsetHint() {
      if (this.formData.units === 'fixed') {
        return (
          this.$t('rate_plans.pricing_offset_fixed_hint') ||
          'Fixed amount adjustment to base price'
        )
      }
      return this.$t('rate_plans.pricing_offset_hint')
    },
  },

  async created() {
    if (!this.channelListings || this.channelListings.length === 0) {
      try {
        await this.fetchChannelListings()
      } catch (e) {
        // keep the field usable even if loading fails
      }
    }

    if (this.ratePlan) {
      this.formData = {
        ...this.formData,
        ...this.ratePlan,
        units: this.ratePlan.units || 'percentage',
        pms_type: 'simple_booking',
      }
    }

    if (!this.formData.units) {
      this.$set(this.formData, 'units', 'percentage')
    }
  },

  methods: {
    ...mapActions('ratePlan', ['createRatePlan', 'updateRatePlan']),
    ...mapActions('simpleBooking', ['fetchChannelListings']),

    closeModal() {
      if (this.loading) {
        return
      }
      this.$store.commit('hideModal')
    },

    async save() {
      this.loading = true
      try {
        const payload = {
          rate_plan: {
            ...this.formData,
            pms_type: 'simple_booking',
          },
        }

        if (this.isEditing) {
          await this.updateRatePlan({
            id: this.ratePlan.id,
            ...payload,
          })
        } else {
          await this.createRatePlan(payload)
        }
      } catch (error) {
        console.error('Error saving Simple Booking rate plan:', error)
      } finally {
        this.loading = false
        this.closeModal()
      }
    },
  },
}
</script>

<style scoped></style>
